import { AesMidasUserDataConfig } from '@orbis-systems/orbis-api-clients/aesmidas';
import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import { AccountStatus, AgreementCode } from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import {
    Agreement,
    AgreementEntry,
    Apis,
    MiddlewareConfig,
    RawUserSettings,
    Rtbs,
    User,
    UserWithExtraInfo,
} from '../types';
import { Account, AccountWithExtraInfo } from '../types/Account.type';
import { BranchApi } from './BranchApi';

@injectable()
export class UserApi extends BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(ApiInjectToken.BranchApi) protected readonly branchApi: BranchApi,
    ) {
        super(apis, config);
    }

    async getUser(userId: number): Promise<UserWithExtraInfo | null> {
        const data = await Promise.all([this.branchApi.getUsers(), this.getAccounts(userId)]);

        const users = data[0];
        const user = users.find((user) => user.userId === userId);
        const userAccounts = data[1];

        if (!user) {
            return null;
        }

        const rtbs = userAccounts.reduce<Rtbs>(
            (result, account) => {
                result.balance += account.balance;
                result.cashOnAccount += account.cashOnAccount;
                result.markToMarket += account.markToMarket;
                result.sodCashBalance += account.sodCashBalance;
                result.sodMarginBalance += account.sodMarginBalance;
                result.sodShortBalance += account.sodShortBalance;
                result.totalDividendAmount += account.totalDividendAmount;
                result.totalDividendYield += account.totalDividendYield;
                return result;
            },
            {
                balance: 0,
                cashOnAccount: 0,
                markToMarket: 0,
                sodCashBalance: 0,
                sodMarginBalance: 0,
                sodShortBalance: 0,
                totalDividendAmount: 0,
                totalDividendYield: 0,
            },
        );

        return {
            ...user,
            ...rtbs,
            accounts: userAccounts,
        };
    }

    async getAccounts(userId: number): Promise<AccountWithExtraInfo[]> {
        const data = (await this.apis.orbis.advisory.userAccountManagement.accounts({ uid: userId, loadRtb: true }))
            .data as {
            account: Account;
            rtb?: Rtbs;
        }[];

        return data
            .filter((item) => item.account.status === AccountStatus.Active)
            .map((item) => {
                const {
                    balance = 0,
                    cashOnAccount = 0,
                    markToMarket = 0,
                    sodCashBalance = 0,
                    sodMarginBalance = 0,
                    sodShortBalance = 0,
                    totalDividendAmount = 0,
                    totalDividendYield = 0,
                } = item.rtb ?? {};

                return {
                    ...item.account,
                    balance,
                    cashOnAccount,
                    markToMarket,
                    sodCashBalance,
                    sodMarginBalance,
                    sodShortBalance,
                    totalDividendAmount,
                    totalDividendYield,
                };
            });
    }

    async setPassword(currentPassword: string, newPassword: string): Promise<{ success: boolean }> {
        // FIXME: missing from docs. Waiting for backend update
        const response = await this.apis.orbis.getApiClient().post<any>('auth/v1/password/set', {
            currentPassword: currentPassword,
            newPassword: newPassword,
        });
        return response.data;
    }

    async resetPassword(username: string): Promise<{ success: boolean }> {
        const response = await this.apis.orbis.user.passwordChange({
            username,
        });
        return response.data;
    }

    async getUserAgreements(): Promise<Agreement[]> {
        const response = await this.apis.orbis.user.agreements.available();
        const agreements = response.data;

        Object.keys(agreements).forEach((key) => {
            agreements[key] = [agreements[key], true];
        });

        const responseUnsigned = await this.apis.orbis.user.agreements.unsigned();
        const unsignedAgreements: AgreementCode[] = responseUnsigned.data;
        unsignedAgreements.forEach((key) => (agreements[key][1] = false));

        const data = Object.keys(agreements).map((key) => ({
            code: key as AgreementCode,
            version: agreements[key][0],
            signed: agreements[key][1],
        }));

        return data;
    }

    async signUserAgreement(code: AgreementCode, entries: AgreementEntry[]): Promise<{ success: boolean }> {
        const response = await this.apis.orbis.user.agreements.sign({
            code,
            entries,
        });
        return response.data;
    }

    async getUserInfo(): Promise<User> {
        const response = await this.apis.orbis.user.userInformation();
        return response.data;
    }

    async getUserInfoV3(): Promise<Omit<User, 'accountList'>> {
        // V3 user info endpoint contains no accountList
        const response = await this.apis.orbis.user.userInformationV3();
        return response.data;
    }

    async getUserSettings(): Promise<RawUserSettings> {
        const response = await this.apis.orbis.user.preferencesView();
        return response.data;
    }

    async setUserSettings(data: RawUserSettings): Promise<RawUserSettings> {
        const response = await this.apis.orbis.user.preferencesChange('set', data);
        return response.data;
    }

    async getAesMidasUserDataConfig(userId: number, b2bUser = false): Promise<AesMidasUserDataConfig> {
        try {
            const response = (await this.apis.aesMidas?.user.getUserDataConfigById({
                user_id: `${b2bUser ? 'b2b' : 'b2c'}_${userId}`,
            })) || {
                data: {} as AesMidasUserDataConfig,
            };
            return response.data;
        } catch {
            return {} as AesMidasUserDataConfig;
        }
    }

    async deleteUserSettings(keys: string[]): Promise<RawUserSettings> {
        const data = keys.reduce<RawUserSettings>((result, key) => {
            result[key] = '';
            return result;
        }, {});
        const response = await this.apis.orbis.user.preferencesChange('delete', data);
        return response.data;
    }
}
